import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useCiAuthStore } from './auth/ciAuthStore';
import { useCiPoolStore } from './authorization/ciPoolStore';
import { useAuthorizedPools } from './authorization/useAuthorizedPools';
import { CiAuthPage } from './auth/CiAuthPage';
import { CiLayout } from './components/CiLayout';
import { OverviewPage } from './pages/OverviewPage';
import { PlayersPage } from './pages/PlayersPage';
import { ActivityFeedPage } from './pages/ActivityFeedPage';
import { ContentOpportunitiesPage } from './pages/ContentOpportunitiesPage';
import { PageViewsPage } from './pages/PageViewsPage';
import './styles/ci-dashboard.css';

/**
 * Entrée du Community Intelligence Dashboard.
 *
 * Monté sur /ci-dashboard/* dans App.tsx, en dehors de <MainLayout /> pour
 * garantir l'isolation visuelle et fonctionnelle (aucun Navbar/Sidebar/Chat
 * de l'app pool ne s'affiche ici).
 *
 * Logique de garde :
 * 1. Non authentifié (ciAuthStore vide) → redirection vers /ci-dashboard/auth.
 * 2. Authentifié mais aucun pool autorisé (dashboardadmin = false partout) →
 *    écran "Accès refusé" statique. Aucune donnée n'est chargée.
 * 3. Authentifié + pools autorisés → layout + pages. Le pool sélectionné est
 *    normalisé à un des pools autorisés (jamais un ID arbitraire).
 */
export function CiDashboardApp() {
  const location = useLocation();
  const userId = useCiAuthStore((s) => s.user_id);
  const logout = useCiAuthStore((s) => s.logout);

  // Route publique : /ci-dashboard/auth
  if (location.pathname.startsWith('/ci-dashboard/auth')) {
    return (
      <Routes>
        <Route path="/auth" element={<CiAuthPage />} />
        <Route path="*" element={<Navigate to="/ci-dashboard/auth" replace />} />
      </Routes>
    );
  }

  // Non authentifié : rediriger vers l'auth dédiée
  if (!userId) {
    return <Navigate to="/ci-dashboard/auth" replace state={{ from: location.pathname }} />;
  }

  return <CiAuthorizedShell onLogout={logout} />;
}

/**
 * Coquille rendue uniquement quand l'utilisateur est authentifié. On isole ce
 * composant pour que useAuthorizedPools ne soit appelé qu'après login (sinon
 * il déclencherait des requêtes inutiles avec user_id null).
 */
function CiAuthorizedShell({ onLogout }: { onLogout: () => void }) {
  const { loading, pools, error } = useAuthorizedPools();
  const selectedLeagueId = useCiPoolStore((s) => s.selectedLeagueId);
  const setSelectedLeagueId = useCiPoolStore((s) => s.setSelectedLeagueId);

  // Normalisation du pool sélectionné : toujours un des pools autorisés.
  useEffect(() => {
    if (loading) return;
    if (pools.length === 0) {
      if (selectedLeagueId !== null) setSelectedLeagueId(null);
      return;
    }
    const stillAuthorized = pools.some((p) => p.league_id === selectedLeagueId);
    if (!stillAuthorized) {
      setSelectedLeagueId(pools[0].league_id);
    }
  }, [loading, pools, selectedLeagueId, setSelectedLeagueId]);

  if (loading) {
    return (
      <div className="ci-dashboard">
        <div className="ci-empty ci-empty--full">Vérification de vos autorisations…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ci-dashboard">
        <div className="ci-empty ci-empty--full">
          <div className="ci-error">{error}</div>
          <button type="button" className="ci-btn" onClick={onLogout}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  if (pools.length === 0) {
    return (
      <div className="ci-dashboard">
        <div className="ci-empty ci-empty--full">
          <h1 className="ci-auth__title">Accès refusé</h1>
          <p>
            Votre compte n&apos;est associé à aucun pool avec droit d&apos;administration du
            dashboard. Contactez l&apos;équipe DynastyPool si vous pensez qu&apos;il s&apos;agit
            d&apos;une erreur.
          </p>
          <button type="button" className="ci-btn" onClick={onLogout}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <CiLayout pools={pools}>
      <Routes>
        <Route index element={<OverviewPage pools={pools} />} />
        <Route path="players" element={<PlayersPage />} />
        <Route path="activity" element={<ActivityFeedPage />} />
        {/* Ancienne page Content Engine, remplacée par Opportunités. */}
        <Route path="content" element={<Navigate to="/ci-dashboard/opportunities" replace />} />
        <Route path="opportunities" element={<ContentOpportunitiesPage pools={pools} />} />
        <Route path="pages" element={<PageViewsPage />} />
        <Route path="connexions" element={<Navigate to="/ci-dashboard/pages" replace />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </CiLayout>
  );
}
